import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { PageHeader, Row, Col, Card, Statistic, Table, Tag, Button } from "antd";
import {
  DashboardOutlined,
  FileTextOutlined,
  DollarOutlined,
  WarningOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { handleApiError, MyBreadcrumb } from "utils";
import { billingInvoiceListService } from "services/billing";
import { statusMeta, money } from "../constants";

export default function BillingDashboard() {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    setIsLoading(true);
    billingInvoiceListService()
      .then((response) => {
        setIsLoading(false);
        setInvoices(response.data.items || []);
      })
      .catch((error) => {
        setIsLoading(false);
        handleApiError(error);
      });
  }, []);

  const sum = (arr, key) => arr.reduce((s, i) => s + (Number(i[key]) || 0), 0);
  const outstanding = sum(
    invoices.filter((i) => ["SENT", "PARTIALLY_PAID", "OVERDUE"].includes(i.status)),
    "amount_due"
  );
  const collected = sum(invoices, "amount_paid");
  const overdueCount = invoices.filter((i) => i.status === "OVERDUE").length;
  const draftCount = invoices.filter((i) => i.status === "DRAFT").length;

  const recent = [...invoices].slice(0, 8);

  const columns = [
    {
      title: "Invoice #",
      dataIndex: "invoice_number",
      key: "invoice_number",
      render: (t, r) => <a onClick={() => history.push("/billing/invoices/view/" + r._id)}>{t}</a>,
    },
    {
      title: "Customer",
      key: "customer",
      render: (t, r) => (r.company && r.company.company_name) || r.bill_to_name || "-",
    },
    {
      title: "Total",
      dataIndex: "grand_total",
      key: "grand_total",
      align: "right",
      render: (t, r) => money(t, r.currency),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (t) => {
        const m = statusMeta(t);
        return <Tag color={m.color}>{m.label}</Tag>;
      },
    },
  ];

  return (
    <div>
      <MyBreadcrumb
        items={[
          { label: "Home", path: "/" },
          { label: "Billing", path: "" },
          { label: "Dashboard", path: "" },
        ]}
      />
      <PageHeader
        ghost={false}
        avatar={{ icon: <DashboardOutlined /> }}
        title="Billing Dashboard"
        extra={[
          <Button
            key="create"
            type="primary"
            shape="round"
            icon={<PlusCircleOutlined />}
            onClick={() => history.push("/billing/invoices/create")}
          >
            New Invoice
          </Button>,
        ]}
      />
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Outstanding"
              value={outstanding}
              precision={2}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Collected"
              value={collected}
              precision={2}
              valueStyle={{ color: "#3f8600" }}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Overdue"
              value={overdueCount}
              valueStyle={{ color: "#cf1322" }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="Drafts" value={draftCount} prefix={<FileTextOutlined />} />
          </Card>
        </Col>
      </Row>
      <Card title="Recent Invoices" style={{ marginTop: 16 }} size="small">
        <Table
          dataSource={recent}
          columns={columns}
          loading={isLoading}
          rowKey="_id"
          pagination={false}
        />
      </Card>
    </div>
  );
}
